import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

export default function FilterByStatus({
  data,
  setFilteredData,
  filterMenu,
  setFilterMenu,
}) {
  const [allMemberData, setAllMemberData] = useState([]);
  const [mileStones, setMileStones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState(false);

  const milestoneRef = useRef(null);
  const assigneeRef = useRef(null);
  const taskStatusRef = useRef(null);
  const taskFinishedCheckboxRef = useRef(null);

  const usersData = useSelector((state) => state.auth.userInfoData);
  const dispatch = useDispatch();

  useEffect(() => {
    const projectId = data?.project?._id;
    if (!projectId) return;

    let mounted = true;
    const listAllMembersData = async () => {
      try {
        const responseListData = await dispatch.people.listAllMembersOnly();
        const getMileStone = await dispatch.projectDetails.getMileStoneList({
          project_id: projectId,
        });

        if (!mounted) return;
        setMileStones(getMileStone || []);
        if (responseListData?.input) {
          setAllMemberData(responseListData.input);
        }
      } catch (err) {
        // ignore or handle logging
      }
    };
    listAllMembersData();
    return () => {
      mounted = false;
    };
  }, [data?.project?._id, dispatch]);

  const options = useMemo(() => {
    if (!usersData)
      return allMemberData.map((u) => ({ value: u.id, label: u.fullName }));
    const first = {
      value: usersData._id,
      label: usersData.last_name
        ? `${usersData.first_name} ${usersData.last_name} (You)`
        : usersData.first_name,
    };
    const others = allMemberData
      .filter((u) => u.id !== usersData._id)
      .map((u) => ({ value: u.id, label: u.fullName }));
    return [first, ...others];
  }, [allMemberData, usersData]);

  const statusOptions = useMemo(
    () => [
      { value: "Todo", label: "Todo" },
      { value: "In Progress", label: "In Progress" },
      { value: "Production", label: "Production" },
      { value: "QA", label: "QA" },
    ],
    []
  );

  const mileStoneOptions = useMemo(
    () => mileStones.map((m) => ({ value: m._id, label: m.title })),
    [mileStones]
  );

  const getFullName = useCallback(
    (id) => {
      const user = allMemberData.find((u) => u.id === id);
      return user
        ? { value: id, label: user.fullName }
        : { value: id, label: id };
    },
    [allMemberData]
  );

  const getMileStoneName = useCallback(
    (id) => {
      const milestone = mileStones.find((m) => m._id === id);
      return milestone
        ? { value: id, label: milestone.title }
        : { value: id, label: id };
    },
    [mileStones]
  );

  const handleClearAll = useCallback(() => {
    milestoneRef.current?.clearValue();
    assigneeRef.current?.clearValue();
    taskStatusRef.current?.clearValue();
    if (taskFinishedCheckboxRef.current)
      taskFinishedCheckboxRef.current.checked = false;

    setFilterMenu({
      milestone_id: [],
      milestone_name: [],
      assigned_to: [],
      assigned_to_name: [],
      task_status: [],
      is_task_finished: false,
      task_due_date: "",
    });
  }, [setFilterMenu]);

  const applyFilter = useCallback(async () => {
    setLoading(true);
    try {
      const expectedState = {
        milestone_id: [],
        milestone_name: [],
        assigned_to: [],
        assigned_to_name: [],
        task_status: [],
        is_task_finished: false,
        task_due_date: "",
      };

      if (JSON.stringify(filterMenu) === JSON.stringify(expectedState)) {
        setValidation(true);
        setTimeout(() => setValidation(false), 2000);
        return;
      }

      const res = await dispatch.projectDetails.getTasksListFilter({
        ...filterMenu,
      });
      if (res?.data) setFilteredData(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [dispatch, filterMenu, setFilteredData]);

  

  return (
    <>
      <div className="flex justify-between">
        <label className="text-xs font-semibold">Milestone</label>
        <button className="text-xs font-bold" onClick={handleClearAll}>
          Clear Filter
        </button>
      </div>

      <div className="mt-1 mb-3">
        <Select
          placeholder="Select Milestone"
          isMulti
          ref={milestoneRef}
          className="w-full text-xs"
          onChange={(selected) => {
            if (!selected || selected.length === 0) {
              setFilterMenu((prev) => ({
                ...prev,
                milestone_id: [],
                milestone_name: [],
              }));
              return;
            }
            setFilterMenu((prev) => ({
              ...prev,
              milestone_id: selected.map((s) => s.value),
              milestone_name: selected.map((s) => getMileStoneName(s.value)),
            }));
          }}
          value={
            filterMenu?.milestone_name?.length
              ? filterMenu.milestone_name.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))
              : []
          }
          isClearable
          options={mileStoneOptions}
        />
      </div>

      <div>
        <label className="text-xs font-semibold">Assign To</label>
        <div className="mt-1 mb-3">
          <Select
            placeholder="Select assignee"
            isMulti
            ref={assigneeRef}
            className="w-full text-xs"
            value={
              filterMenu?.assigned_to_name?.length
                ? filterMenu.assigned_to_name.map((item) => ({
                    value: item.value,
                    label: item.label,
                  }))
                : []
            }
            options={options}
            onChange={(selected) => {
              const list = selected || [];
              setFilterMenu((prev) => ({
                ...prev,
                assigned_to: list.map((s) => s.value),
                assigned_to_name: list.map((s) => getFullName(s.value)),
              }));
            }}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold">Status</label>
        <div className="mt-1 mb-3">
          <Select
            placeholder="Select Status"
            isMulti
            ref={taskStatusRef}
            className="w-full text-xs"
            onChange={(selected) => {
              const list = selected || [];
              setFilterMenu((prev) => ({
                ...prev,
                task_status: list.map((s) => s.value),
              }));
            }}
            value={
              filterMenu?.task_status?.length
                ? filterMenu.task_status.map((s) => ({ value: s, label: s }))
                : []
            }
            options={statusOptions}
          />
        </div>
      </div>

      <div className="flex justify-between space-x-3">
        <div className="w-full">
          <label className="text-xs font-semibold">Due Date</label>
          <div className="border border-theme-cream-3 relative rounded-[4px]">
            <div className="flex items-center absolute top-4 px-3">
              <input
                ref={taskFinishedCheckboxRef}
                className="scale-125 accent-gray-700 cursor-pointer"
                type="checkbox"
                checked={!!filterMenu.is_task_finished}
                onChange={(e) =>
                  setFilterMenu((prev) => ({
                    ...prev,
                    is_task_finished: e.target.checked,
                  }))
                }
                disabled={!!filterMenu.task_due_date}
              />
            </div>

            <div className="flex items-center rounded-md ml-10 border-l border-theme-cream-3">
              <input
                className="p-3 pl-5 w-full text-xs outline-none"
                type="date"
                onChange={(e) =>
                  setFilterMenu((prev) => ({
                    ...prev,
                    task_due_date: e.target.value,
                  }))
                }
                value={filterMenu.task_due_date || ""}
                placeholder="Select due date"
                disabled={!!filterMenu.is_task_finished}
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={applyFilter}
        className="mt-6 borderborder-theme-ink bg-theme-ink rounded-md text-tiny font-semibold text-white py-3 px-5 flex gap-2 items-center"
      >
        {loading ? "Applying..." : "Apply"}
      </button>

      <p
        className={`mt-2 text-theme-full-fg transition-all duration-300 ${
          validation ? "opacity-100" : "opacity-0"
        }`}
      >
        Fill the filter fields first.
      </p>
    </>
  );
}
